import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { globalSearch } from '../services/api';
import { HiOutlineSearch, HiOutlineFolder, HiOutlineClipboardList, HiOutlineDocumentText } from 'react-icons/hi';

export default function GlobalSearchModal({ show, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ projects: [], requirements: [], test_cases: [] });
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // Flatten results into a single list for keyboard navigation
  const flatResults = [
    ...results.projects.map((p) => ({ type: 'project', ...p })),
    ...results.requirements.map((r) => ({ type: 'requirement', ...r })),
    ...results.test_cases.map((tc) => ({ type: 'test_case', ...tc })),
  ];

  // Focus input on open
  useEffect(() => {
    if (show) {
      setQuery('');
      setResults({ projects: [], requirements: [], test_cases: [] });
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [show]);

  // Debounced search
  const doSearch = useCallback(async (q) => {
    if (q.length < 2) {
      setResults({ projects: [], requirements: [], test_cases: [] });
      return;
    }
    setLoading(true);
    try {
      const { data } = await globalSearch(q);
      setResults(data);
      setSelectedIndex(0);
    } catch {
      setResults({ projects: [], requirements: [], test_cases: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  // Navigate to the selected result
  const goToResult = (item) => {
    if (!item) return;
    let path = '';
    if (item.type === 'project') {
      path = `/projects/${item.id}`;
    } else if (item.type === 'requirement') {
      path = `/projects/${item.project_id}?tab=requirements&highlight=${item.id}`;
    } else if (item.type === 'test_case') {
      path = `/projects/${item.project_id}?tab=tests&highlight=${item.id}`;
    }
    onClose();
    if (path) {
      setTimeout(() => navigate(path), 100);
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flatResults[selectedIndex]) goToResult(flatResults[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!show) return null;

  const hasResults = flatResults.length > 0;
  const noResults = query.length >= 2 && !loading && !hasResults;
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

  const renderItem = (item, globalIdx) => {
    let id = '', label = '', extra = null;
    if (item.type === 'project') {
      id = item.code || '';
      label = item.name || '(sans nom)';
    } else if (item.type === 'requirement') {
      id = item.exg_id || '';
      label = item.exigence || '(sans titre)';
      extra = item.type_exigence || '';
    } else if (item.type === 'test_case') {
      id = item.ct_id || '';
      label = item.cas_test || '(sans titre)';
      extra = item.statut || '';
    }

    return (
      <button
        type="button"
        key={`${item.type}-${item.id}`}
        className={`global-search-item${globalIdx === selectedIndex ? ' selected' : ''}`}
        onClick={() => goToResult(item)}
        onMouseEnter={() => setSelectedIndex(globalIdx)}
        style={{ padding: '8px 16px', cursor: 'pointer', color: isDark ? '#e5e7eb' : '#1a202c', background: 'none', border: 'none', font: 'inherit', textAlign: 'left', width: '100%', display: 'block' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            color: isDark ? '#60a5fa' : '#2563eb',
            background: isDark ? 'rgba(96,165,250,0.12)' : 'rgba(37,99,235,0.08)',
            fontSize: '0.72rem',
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 4,
            flexShrink: 0,
          }}>{id}</span>
          <span style={{
            color: isDark ? '#f9fafb' : '#111827',
            fontWeight: 500,
            fontSize: '0.84rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}>{label}</span>
          {extra && (
            <span style={{
              color: isDark ? '#9ca3af' : '#6b7280',
              fontSize: '0.7rem',
              flexShrink: 0,
            }}>{extra}</span>
          )}
        </div>
      </button>
    );
  };

  const renderSection = (title, Icon, items, startIndex) => {
    if (!items || items.length === 0) return null;
    return (
      <div style={{ padding: '4px 0' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 16px',
          fontSize: '0.72rem',
          fontWeight: 600,
          color: isDark ? '#9ca3af' : '#374151',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}>
          {Icon} {title}
        </div>
        {items.map((item, i) => renderItem(item, startIndex + i))}
      </div>
    );
  };

  const projStart = 0;
  const reqStart = results.projects.length;
  const tcStart = reqStart + results.requirements.length;

  return (
    <div className="global-search-overlay">
      <button type="button" className="global-search-overlay-btn" onClick={onClose} aria-label="Fermer" tabIndex={-1} style={{ position: 'absolute', inset: 0, background: 'transparent', border: 'none', cursor: 'default', zIndex: 0, outline: 'none' }} />
      <dialog
        className="global-search-container"
        open
        aria-modal="true"
        style={{ background: isDark ? '#1e2028' : '#ffffff', color: isDark ? '#e5e7eb' : '#1a202c' }}
      >
        <div className="global-search-input-wrapper">
          <HiOutlineSearch className="global-search-icon" />
          <input
            ref={inputRef}
            type="text"
            className="global-search-input"
            placeholder="Rechercher projets, exigences, cas de test..."
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            style={{ color: isDark ? '#f3f4f6' : '#1a202c' }}
          />
          <kbd className="global-search-kbd">Esc</kbd>
        </div>
        {(hasResults || noResults || loading) && (
          <div className="global-search-results">
            {loading && <div className="global-search-loading">Recherche en cours...</div>}
            {noResults && <div className="global-search-empty">Aucun resultat pour "{query}"</div>}
            {renderSection('Projets', <HiOutlineFolder />, results.projects.map(p => ({type:'project',...p})), projStart)}
            {renderSection('Exigences', <HiOutlineClipboardList />, results.requirements.map(r => ({type:'requirement',...r})), reqStart)}
            {renderSection('Cas de test', <HiOutlineDocumentText />, results.test_cases.map(tc => ({type:'test_case',...tc})), tcStart)}
          </div>
        )}
        <div className="global-search-footer">
          <span><kbd>&uarr;</kbd> <kbd>&darr;</kbd> naviguer</span>
          <span><kbd>Entree</kbd> ouvrir</span>
          <span><kbd>Esc</kbd> fermer</span>
        </div>
      </dialog>
    </div>
  );
}
